/* eslint-disable no-undef */
/* eslint-disable no-alert */

import lodash from "lodash";
import pako from "pako";

import {
  getReadableColumnsErrorMessage,
  getReadableRowsErrorMessage,
  formatCpf,
  formatCep,
  validateTypeUser,
  validateAndNormalizeDocument
} from "./utils";

import parseCsv from "./utils/parseCsv";
import validators from "./utils/validators";
import { SERVER_URL } from "./config";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader(); //eslint-disable-line
    reader.readAsDataURL(file.rawFile);

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const readFileAsText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader(); //eslint-disable-line
    reader.readAsText(file.rawFile);

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const picturesArray = (images) => {
  const formerPictures = images.filter((p) => !(p.rawFile instanceof File));
  const newPictures = images.filter((p) => p.rawFile instanceof File);

  return Promise.all(newPictures.map(readFileAsDataUrl))
    .then((base64Pictures) =>
      base64Pictures.map((picture64, index) => ({
        src: picture64,
        title: `${newPictures[index].title}`,
        type: newPictures[index].rawFile.type,
      }))
    )
    .then((transformedNewPictures) => [transformedNewPictures, ...formerPictures]);
};

const normalizePanelRows = (rows) => rows.map((row) => {
  const { normalizedDocument } = validateAndNormalizeDocument(row.documento);

  return {
    ...row,
    documento: normalizedDocument,
    cpf: row.cpf ? formatCpf(row.cpf) : row.cpf,
    cep: formatCep(row.cep),
  };
});

const addUploadFeature = (requestHandler) => async (type, resource, params) => {
  const { create, getList, getOne, getMany, update, delete: deleteResource } = requestHandler;
  if(params.pagination?.perPage > 500 && resource === "panels"){
    return getList('panels/export', params);
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "paywalls") {
    let imageMobile;
    let imageDesktop;

    if (params.data.image_mobile && params.data.image_mobile.src) {
      imageMobile = await picturesArray([params.data.image_mobile]);
    }

    if (params.data.image_desktop && params.data.image_desktop.src) {
      imageDesktop = await picturesArray([params.data.image_desktop]);
    }

    return requestHandler.update(resource, {
      ...params,
      data: {
        ...params.data,
        image_mobile: imageMobile,
        image_desktop: imageDesktop,
      },
    });
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "ads") {
    let iconImage;
    let finalUrl;

    if (params.data.icon && params.data.icon.src) {
      iconImage = await picturesArray([params.data.icon]);
    }

    const hasUrlFileRaw =
      params.data.urlFile &&
      (params.data.urlFile.rawFile instanceof File || params.data.urlFile instanceof File);
    const hasUrlRaw =
      params.data.url && (params.data.url.rawFile instanceof File || params.data.url instanceof File);

    if (hasUrlFileRaw || hasUrlRaw) {
      const MAX_FILE_MB = 25;
      const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

      const sourceField = hasUrlFileRaw ? params.data.urlFile : params.data.url;
      const rawFile =
        sourceField && sourceField.rawFile instanceof File
          ? sourceField.rawFile
          : sourceField instanceof File
          ? sourceField
          : null;

      if (rawFile && rawFile.size > MAX_FILE_BYTES) {
        throw new Error(`O arquivo excedeu o limite de ${MAX_FILE_MB}MB. Envie um arquivo menor.`);
      }

      finalUrl = await picturesArray([sourceField]);
    }

    let urlPayload = params.data.url;
    if (finalUrl !== undefined) {
      urlPayload = finalUrl;
    }

    const { urlFile, ...restData } = params.data;
    const parameter = {
      ...params,
      data: {
        ...restData,
        icon: iconImage,
        url: urlPayload,
      }
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }

  if (type === "CREATE" && resource === "representatives") {
    let avatarImage;
    let newCsv;

    if (params.data.avatar && params.data.avatar.src) {
      avatarImage = await picturesArray([params.data.avatar]);
    }

    if (params.data.csv && params.data.csv.src) {
      await readFileAsText(params.data.csv).then((result) => {
        newCsv = result;
      });
    }

    const rows = await parseCsv(newCsv, "representatives").catch((e) => {
      throw new Error(e);
    });
    if (!rows || !rows.length) throw new Error("CSV inválido");

    const columns = Object.keys(rows[0]).map((column) => column);
    const validHeaders = validators.validateCsvHeaders(columns, "representatives");

    if (validHeaders && validHeaders.errors) {
      const errorMessage = getReadableColumnsErrorMessage(validHeaders.errors);

      alert(`Revise o nome da(s) coluna(s):\r\n${errorMessage}`);

      throw new Error("CSV inválido, revise o nome das colunas");
    }

    const valid = await validators.validateCsvList(rows, "representatives");

    if (valid && valid.errors) {
      const errorMessage = getReadableRowsErrorMessage(valid.errors);

      const errorsQuantity = valid.errors.length;

      alert(`Foram encontrados ${errorsQuantity} erros:\r\n${errorMessage}`);

      throw new Error("CSV inválido");
    }

    return create(resource, {
      ...params,
      data: {
        ...params.data,
        csv: newCsv,
        avatar: avatarImage,
      },
    });
  }

  if (type === "UPDATE" && resource === "representatives") {
    const { data } = params;
    let avatarImage;

    if (params.data.avatar && params.data.avatar.src) {
      avatarImage = await picturesArray([params.data.avatar]);
    }

    const parameter = {
      id: data.id,
      name: data.name,
      phone: data.phone,
      email: data.email,
      roleUser: {
        id: data.roleUser.name === "manager" ? 2 : 1,
        name: data.roleUser.name,
        updatedAt: data.roleUser.updatedAt,
      },
      avatar: avatarImage,
    };

    return update(resource, {
      ...parameter,
      data: {
        ...parameter,
        avatar: avatarImage,
      },
    });
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "labs") {
    let logoImage;

    if (params.data.logo && params.data.logo.src) {
      logoImage = await picturesArray([params.data.logo]);
    }

    const parameter = {
      ...params,
      data: {
        ...params.data,
        logo: logoImage,
      },
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }


  if (type === "CREATE" && resource === "orders") {
    return create(resource, params);
  }

  if (type === "CREATE" && resource === "orders/nfe-uploads") {
    let xmlFile;
    if (params.data.file && params.data.file.rawFile) {
      xmlFile = await readFileAsText(params.data.file);
    } else if (params.data.file instanceof File) {
      xmlFile = await readFileAsText({ rawFile: params.data.file });
    } else {
      throw new Error("Arquivo XML não selecionado.");
    }

    const payload = {
      labId: params.data.labId,
      xmlContent: xmlFile,
      filename: params.data.file.rawFile ? params.data.file.rawFile.name : params.data.file.name,
    };
    const jsonString = JSON.stringify(payload);

    const token = localStorage.getItem("token");
    const response = await fetch(`${SERVER_URL}/orders/upload-nfe-xml`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: jsonString,
    });

    const messageBack = await response.text();
    if (!response.ok) {
      let errorMsg = "Erro ao enviar arquivo";
      try {
        const parsed = JSON.parse(messageBack);
        if (parsed && parsed.message) {
          errorMsg = parsed.message;
        } else if (messageBack) {
          errorMsg = messageBack;
        }
      } catch {
        if (messageBack) errorMsg = messageBack;
      }
      throw new Error(errorMsg);
    }

    return { data: { id: Date.now(), labId: params.data.labId, filename: payload.filename } };
  }

  if (type === "CREATE" && resource === "sectors") {
    let newCsv;
    if (params.data.csv && params.data.csv.src) {
      await readFileAsText(params.data.csv).then((result) => {
        newCsv = result;
      });
    }

    const rows = await parseCsv(newCsv, "sectors").catch((e) => {
      throw new Error(e);
    });
    if (!rows || !rows.length) throw new Error("CSV inválido");

    const validationType = params.data.isGD ? "sectors-gd" : "sectors";
    const columns = Object.keys(rows[0]).map((column) => column);
    const validHeaders = validators.validateCsvHeaders(columns, validationType);

    if (validHeaders && validHeaders.errors) {
      const errorMessage = getReadableColumnsErrorMessage(validHeaders.errors);

      alert(`Revise o nome da(s) coluna(s):\r\n${errorMessage}`);

      throw new Error("CSV inválido, revise o nome das colunas");
    }

    const valid = await validators.validateCsvList(rows, validationType);
    if (valid && valid.errors) {
      const errorMessage = getReadableRowsErrorMessage(valid.errors);

      const errorsQuantity = valid.errors.length;

      alert(`Foram encontrados ${errorsQuantity} erros:\r\n${errorMessage}`);

      throw new Error("CSV inválido");
    }

    const parameter = {
      ...params,
      data: {
        isGD: params.data?.isGD,
        lab: params.data?.lab,
        csv: newCsv,
      },
    };

    return create(resource, parameter);

  }

  if (type === "UPDATE" && resource === "sectors") {
    let newCrmList;
    if (params.data.crmList && params.data.crmList.src) {
      await readFileAsText(params.data.crmList).then((result) => {
        newCrmList = result;
      });
    }

    return requestHandler(type, resource, {
      ...params,
      data: {
        ...params.data,
        crmList: newCrmList,
      },
    });
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "kits") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "kitImport") {
    let newCsv;
    if (params.data.csv && params.data.csv.src) {
      await readFileAsText(params.data.csv).then((result) => {
        newCsv = result;
      });
    }

    const rows = await parseCsv(newCsv, "kitImport").catch((e) => {
      throw new Error(e);
    });
    if (!rows || !rows.length) throw new Error("CSV inválido");

    const columns = Object.keys(rows[0]).map((column) => column);
    const validHeaders = validators.validateCsvHeaders(columns, "kitImport");

    if (validHeaders && validHeaders.errors) {
      const errorMessage = getReadableColumnsErrorMessage(validHeaders.errors);

      alert(`Revise o nome da(s) coluna(s):\r\n${errorMessage}`);

      throw new Error("CSV inválido, revise o nome das colunas");
    }

    const parameter = {
      ...params,
      data: {
        ...lodash.omit(params.data),
        kitList: rows,
      },
    };
    return create(resource, parameter);

  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "panels") {
    let newCsv;
    const sourceFile =
      (params.data.panels && params.data.panels.src && params.data.panels) ||
      (params.data.csv && params.data.csv.src && params.data.csv) ||
      params.data.csv ||
      params.data.panels;

    if (sourceFile && sourceFile.src) {
      await readFileAsText(sourceFile).then((result) => {
        newCsv = result;
      });
    }
    const rows = await parseCsv(newCsv, "panels").catch((e) => {
      throw new Error(e);
    });

    if (!rows || !rows.length) throw new Error("CSV inválido");

    const normalizedPanelRows = normalizePanelRows(rows);

    const columns = Object.keys(normalizedPanelRows[0]).map((column) => column);
    const validHeaders = validators.validateCsvHeaders(columns, "panels");
    if (validHeaders && validHeaders.errors) {
      const errorMessage = getReadableColumnsErrorMessage(validHeaders.errors);
      throw new Error(`Revise o nome da(s) coluna(s):\r\n${errorMessage}`);
    }

    const typeIsInvalid = normalizedPanelRows
      .map((row) => validateTypeUser(row.tipo))
      .includes(false);
    const documentIsInvalid = normalizedPanelRows
      .map((row) => validateAndNormalizeDocument(row.documento).isValid)
      .includes(false);

    if (documentIsInvalid) {
      throw new Error("Foram encontrados erros no campo: Documento");
    }

    if (typeIsInvalid || documentIsInvalid) {
      throw new Error(
        `Foram encontrados erros no campo: ${typeIsInvalid ? "Tipo" : ""} ${
          typeIsInvalid && documentIsInvalid ? " e " : ""
        } ${documentIsInvalid ? "Documento" : ""}`
      );
    }

    const data = type === "UPDATE" ? lodash.omit(params.data, "user") : lodash.omit(params.data);

    const parameter = {
      ...params,
      data: {
        ...data,
        ...(type === "CREATE" ? { panelList: normalizedPanelRows } : { panelEditList: normalizedPanelRows }),
      },
    };

    if (type === "CREATE") {
      return create(resource, parameter);
    }

    return update(resource, parameter).then((result) => {
      if (!result?.data?.id) {
        return { data: { ...params.data, id: params.id } };
      }
      return result;
    });
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "samples") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "surveys") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }

  if ((type === "CREATE" || type === "UPDATE" || type === "DELETE") && resource === "users") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    if (type === "CREATE") {
      return create(resource, parameter);
    } else if (type === "UPDATE") {
      return update(resource, parameter);
    } else if (type === "DELETE") {
      return deleteResource(resource, params);
    }
  }

  if (type === "CREATE" && resource === "addresses") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    return create(resource, parameter);
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "groups") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }

  if ((type === "CREATE" || type === "UPDATE") && resource === "projects") {
    const parameter = {
      ...params,
      data: {
        ...params.data,
      },
    };

    return type === "CREATE" ? create(resource, parameter) : update(resource, parameter);
  }

  if (type === "CREATE" && resource === "consolidated-delivered-orders") {
    let csvFile;

    if (params.data.file && params.data.file.rawFile) {
      await readFileAsText(params.data.file).then((result) => {
        csvFile = result;
      });
    }

    const payload = { ...params.data, file: csvFile };
    const jsonString = JSON.stringify(payload);
    const compressed = pako.gzip(new TextEncoder().encode(jsonString));

    const COMPRESSED_SIZE_LIMIT_MB = 20;
    if (compressed.length > COMPRESSED_SIZE_LIMIT_MB * 1024 * 1024) {
      throw new Error(
        `O arquivo excedeu o limite de tamanho. Envie um arquivo menor.`
      );
    }

    const token = localStorage.getItem("token");
    const response = await fetch(`${SERVER_URL}/consolidated-delivered-orders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Content-Encoding": "gzip",
        Authorization: `Bearer ${token}`,
      },
      body: compressed,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Erro ao enviar" }));
      throw new Error(error.message || "Erro ao criar integração");
    }

    const json = await response.json();
    return { data: { ...json, id: json.id || params.data.id } };
  }

  if (type === "GET_LIST" && resource === "consolidated-delivered-orders") {
    const parameter = {
      ...params,
      pagination: params.pagination || { page: 1, perPage: 25 },
    };
    return getList(resource, parameter);
  }

  if (type === "GET_LIST" && resource === "address/type-address") {
    const response = await getList(resource, params);
    return {
      ...response,
      data: response.data.data || response.data,
    };
  }

  if ((type === "GET_LIST") && resource === "panels" && params.filter?.createdAt) {
    const parameter = {
      ...params,
      filter: {
        ...params.filter,
        createdAt: {"gte": params.filter.createdAt,"lte": params.filter.createdAt},
      },
    };

    return getList(resource, parameter);
  }


  if ((type === "GET_LIST") && resource === "opt-in-users") {
    const { data, total } = await getList(resource, params);

    const manipulatedData = data.map((item) => ({ ...item, 
      sampleLabs: item.labs?.slice(0, 2), 
      sampleTherapeuticCategories: item.therapeuticCategories?.slice(0, 2), 
      categoriesLength: item.therapeuticCategories?.length,
      labsLength: item.labs?.length, 
    }))
  
    return { data: manipulatedData, total};
  }

  return type === "GET_ONE" && params.id
    ? getOne(resource, params)
    : type === "GET_MANY"
    ? getMany(resource, params)
    : getList(resource, params);
};

export default addUploadFeature;
